import {Component} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Toaster, Type} from '../../toaster/Toaster';
import {BrowserUtil} from '../../utils/browser.util';
import {ActivatedRoute, Router} from '@angular/router';
import {ApiServiceService} from '../../api-service.service';
import {PortfolioType} from '../../model/portfolio-type.model';
import {PortfolioService} from '../../service/portfolio.service';
import {Keys, StorageService} from '../../service/storage.service';
import {EntityConflictError} from '../../errors/EntityConflictError';
import {GaugedInvestment} from '../../model/gauged-investment.model';
import {PreferredCategories} from '../../model/portfolio-preferences.model';
import {FullPageLoader, LoaderState} from '../../full-page-loader/full-page-loader';
import {ConfirmPopUpComponent, ConfirmPopUpData} from '../../comfirm-pop-up/confirm-pop-up.component';
import {AutoInvestPortfolioCreationData} from '../../model/portfolio-creation-data/auto-invest-portfolio-creation.data';

@Component({
    templateUrl: './auto-invest.component.html',
    providers: [ApiServiceService, PortfolioService],
    styleUrls: ['../../../assets/styles/finzy-styles.scss', './auto-invest.component.scss']
})
export class AutoInvestComponent {
    private static readonly PORTFOLIO_NAME = 'auto_invest';
    public remainingAmount: number;
    public portfolioAmount: number;
    public gaugedInvestments: GaugedInvestment[];
    public portfolioCreationData: AutoInvestPortfolioCreationData;
    public isFullScreenLoaderShown: boolean;
    private readonly router: Router;
    private readonly matDialog: MatDialog;
    private readonly browserUtil: BrowserUtil;
    private readonly storageService: StorageService;
    private readonly portfolioService: PortfolioService;
    private readonly fullScreenLoader: FullPageLoader;

    constructor(router: Router, matDialog: MatDialog, storageService: StorageService, portfolioService: PortfolioService,
                browserUtil: BrowserUtil, activatedRoute: ActivatedRoute, fullScreenLoader: FullPageLoader) {
        this.router = router;
        this.matDialog = matDialog;
        this.storageService = storageService;
        this.portfolioService = portfolioService;
        this.browserUtil = browserUtil;
        this.fullScreenLoader = fullScreenLoader;
        activatedRoute.data.subscribe(({portfolioCreationData}: { portfolioCreationData: AutoInvestPortfolioCreationData }) => {
            this.onPortfolioCreationDataReceived(portfolioCreationData);
        });
        FullPageLoader.subscribeToLoaderInstructions(value => this.isFullScreenLoaderShown = value === LoaderState.Shown);
    }

    public async onInvestmentCancelled() {
        const matDialogRef = this.matDialog.open(ConfirmPopUpComponent, {
            data: <ConfirmPopUpData>{
                title: 'Are you sure you want to exit?',
                message: 'These loans might not be available later. Do you really want to exit the investment flow?',
                positiveButtonText: 'Yes, I want to exit',
                negativeButtonText: 'No, take me back'
            }
        });
        const result = await matDialogRef.afterClosed().toPromise();
        if (result) {
            this.navigateToDashboard();
        }
    }

    public async createPortfolio(gaugedInvestments: GaugedInvestment[]) {
        try {
            const isPreFlex = this.storageService.getItem(Keys.FINX) === "true" && this.storageService.getItem(Keys.IS_LENDER_CREATED_AFTER_RESTRICTED_DATE) === "true";
            const preFlexAggrement: boolean = this.storageService.getObjectFromSession(Keys.FINZY_FLEX_PRE_LAUNCH_AGGREMENT);
            await this.portfolioService.createPortfolio(gaugedInvestments, await this.storageService.getObjectFromSession(Keys.LENDER_LOAN_AGREEMENT_REVIEWED),
                true, await this.storageService.getObjectFromSession(Keys.LENDER_10LAKH_LIMIT_CHECK),
                PortfolioType.AutoInvest, this.getPortfolioAmount(), AutoInvestComponent.PORTFOLIO_NAME,
                this.portfolioCreationData.portfolioPreference, (isPreFlex) && preFlexAggrement);
        } catch (e) {
            if (e instanceof EntityConflictError) {
                await this.matDialog.open(ConfirmPopUpComponent, {
                    data: <ConfirmPopUpData>{
                        title: 'Uh oh!',
                        message: 'One or more of the loans that we had picked for you seem to have been invested in ' +
                            'by other investors and are no longer available to you. We are picking loans again for you.',
                        positiveButtonText: 'Ok'
                    }
                }).afterClosed().toPromise();
                this.getAndShowEligibleLoans();
                return;
            }
            Toaster.toast('Something went wrong while making your investment.', Type.Error);
        }
        const message = 'Great Choice! Your investment is complete.' + (this.remainingAmount === 0 ? ''
            : ' We will auto invest the remaining amount in new loans that get listed on finzy.');
        Toaster.toast(message, Type.Success);
        this.navigateToDashboard();
    }

    public onPortfolioCreationDataReceived(portfolioCreationData: AutoInvestPortfolioCreationData) {
        this.portfolioCreationData = portfolioCreationData;
        this.getAndShowEligibleLoans();
    }

    public showFullScreenLoader() {
        this.fullScreenLoader.showLoader();
    }

    public hideFullScreenLoader() {
        this.fullScreenLoader.hideLoader();
    }

    navigateToDashboard() {
        this.router.navigate(['/investordashboard']);
    }

    public async getAndShowEligibleLoans() {
        this.browserUtil.scrollToTop();
        this.showFullScreenLoader();

        const gaugedInvestments = await this.portfolioService.getPotentialInvestments(this.getPortfolioAmount(),
            this.storageService.getItem(Keys.LENDER_ID), PortfolioType.AutoInvest, this.portfolioCreationData.portfolioPreference);
        await this.onGaugedInvestmentsReceived(gaugedInvestments);

        this.hideFullScreenLoader();
    }

    public async onGaugedInvestmentsReceived(gaugedInvestments: GaugedInvestment[]) {
        this.gaugedInvestments = gaugedInvestments;

        if (gaugedInvestments.length > 0) {
            const portfolioInvestmentTotal = gaugedInvestments.map(investment => investment.amount)
                .reduce((acc, val) => acc += val, 0);
            this.remainingAmount = this.getPortfolioAmount() - portfolioInvestmentTotal;
        }
    }

    public async onPreferredCategoriesChanged(preferredCategories: PreferredCategories[]) {
        this.portfolioCreationData.portfolioPreference = preferredCategories;
        await this.portfolioService.createOrUpdateLocalPortfolioCreationData(this.portfolioCreationData);
        this.onPortfolioCreationDataReceived(this.portfolioCreationData);
    }

    private getPortfolioAmount() {
        return this.portfolioCreationData.portfolioAmount;
    }
}
